import { useState, useCallback } from 'react';

export interface UploadedFile {
    id: string;
    name: string;
    type: 'photo' | 'document' | 'video' | 'audio';
    url: string;
    size: number;
    uploadedAt: Date;
    description?: string;
    preview?: string;
}

const getFileType = (mimeType: string): UploadedFile['type'] => {
    if (mimeType.startsWith('image/')) return 'photo';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
};

export const useFileUploadLogic = (
    files: UploadedFile[],
    onFilesChange: (files: UploadedFile[]) => void,
    maxFiles: number,
    maxSizeMB: number,
    disabled: boolean
) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const readFileAsDataURL = (file: File): Promise<string> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
        });
    };

    const processFiles = useCallback(async (fileList: FileList) => {
        if (disabled) return;
        setError(null);
        setUploading(true);

        const newFiles: UploadedFile[] = [];
        const maxSizeBytes = maxSizeMB * 1024 * 1024;

        for (const file of Array.from(fileList)) {
            if (files.length + newFiles.length >= maxFiles) {
                setError(`Maximum ${maxFiles} files allowed`);
                break;
            }

            if (file.size > maxSizeBytes) {
                setError(`File "${file.name}" exceeds ${maxSizeMB}MB limit`);
                continue;
            }

            const fileType = getFileType(file.type);
            const dataUrl = await readFileAsDataURL(file);
            const preview = fileType === 'photo' ? dataUrl : undefined;

            newFiles.push({
                id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                name: file.name,
                type: fileType,
                url: dataUrl,
                size: file.size,
                uploadedAt: new Date(),
                preview,
            });
        }

        if (newFiles.length > 0) {
            onFilesChange([...files, ...newFiles]);
        }
        setUploading(false);
    }, [disabled, files, maxFiles, maxSizeMB, onFilesChange]);

    return {
        uploading,
        error,
        setError,
        processFiles
    };
};
